import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Input, InputGroup, InputGroupText, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Spinner } from "reactstrap";
import { useEffect, useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from 'react-toastify';
import _ from 'lodash';

import { fetchItemLookupByFilter } from "../data/itemLookupService";
import { getAllItemLookupTypes } from "../data/itemLookupTypeService";
import { addModuleRevisionLookupItem, removeModuleRevisionLookupItem } from "../../modules/data/moduleRevisionLookupItemService";
import { fetchTags } from "../../tags/data/tagService";
import { fetchSuppliers } from "../../suppliers/data/supplierService";
import { downloadItemsTemplate, exportItems } from "../data/importExportItemsService";
import { downloadFile } from "../../../utils/fileUtils";
import DialogBox from "../../../components/DialogBox";

import DataTablePageQuery from "../../../components/DataTablePageQuery";
import ItemModal from "./ItemModal";
import { formatCurrency } from "../../../utils/numberUtils";
import { formatDateTime } from "../../../utils/dateUtils";
import { useTheme } from "../../../hooks/useTheme";
import TagsList from "../../tags/components/TagsList";
import ItemBulkImport from "./ItemBulkImport";
import { useLockState } from "../../../components/userLocks/LockCheck";
import { fetchSalesforceCodes } from "../../salesforceCodes/data/salesforceCodeService";

const ItemsTable = ({ moduleRevisionId, mode = "Admin", allowShowingInactiveItems = false, onAddItemToBasketClicked, onAddItemToImport, onItemAddedToModule, onItemRemovedFromModule }) => {
    const theme = useTheme();
    const lockState = useLockState();

    // general state
    const [selectedItemModal, setSelectedItemModal] = useState(false);
    const [isIncompleteCatalogueItemDialogOpen, setIsIncompleteCatalogueItemDialogOpen] = useState(false);

    const [exportButtonIsEnabled, setExportButtonIsEnabled] = useState(true);
    const [settingsModalOpen, setSettingsModalOpen] = useState(false);

    const [importModalOpen, setImportModalOpen] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);

    // item state
    const [items, setItems] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);

    // dropdown state
    const [dropdownOpen, setDropdownOpen] = useState(false);

    // pagination state
    const [filter, setFilter] = useState({ pageNumber: 1, pageSize: 10, onlyShowActiveItems: "Active" });
    const [totalRecords, setTotalRecords] = useState(0);

    // column settings in localStorage
    const COLUMN_SETTINGS_KEY = moduleRevisionId ? "moduleItemsTableColumnSettings" :
        mode.toLowerCase() + "ItemsTableColumnSettings";

    const toggleFullscreen = () => setFullscreen((prev) => !prev);

    const toggle = () => setDropdownOpen((prevState) => !prevState);
    const toggleSettingsModal = () => setSettingsModalOpen(!settingsModalOpen);

    // Fetch Item Lookup Types using useQuery
    const {
        data: itemLookupTypesData,
        isLoading: isLoadingItemLookupTypes,
        error: itemLookupTypesError,
    } = useQuery({
        queryKey: ["itemLookupTypesFilter"],
        queryFn: getAllItemLookupTypes,
        staleTime: 60000
    });

    // Fetch Tags using useQuery
    const {
        data: tagsData,
        isLoading: isLoadingTags,
        error: tagsError,
    } = useQuery({
        queryKey: ["tagsFilter"],
        queryFn: fetchTags,
        staleTime: 60000
    });

    // Fetch Suppliers using useQuery
    const {
        data: suppliersData,
        isLoading: isLoadingSuppliers,
        error: suppliersError,
    } = useQuery({
        queryKey: ["itemLookupSuppliersFilter"],
        queryFn: fetchSuppliers,
        staleTime: 60000
    });

    const {
        data: salesforceCodes,
    } = useQuery({
        queryKey: ["salesforceCodes"],
        queryFn: fetchSalesforceCodes,
        staleTime: 60000
    });

    const spreadableUpfrontSalesforceCodeNames = useMemo(() =>
        new Set((salesforceCodes || []).filter(x => !x.disabled && x.type === "Recurring" && x.canBePaidUpfront).map(x => x.name))
    , [salesforceCodes]);

    const getClosestPastVersion = (versions) => versions
        .filter(r => new Date(r.activeStartDate) < new Date())
        .sort((a, b) => new Date(b.activeStartDate) - new Date(a.activeStartDate))[0];

    const getItems = async () => {
        const items = await fetchItemLookupByFilter(filter);
        setTotalRecords(items.totalRecords);
        if (items.totalRecords === 0) return []

        const filteredItems = mode.toLowerCase() === "user" ? items.itemLookupItems.filter(x => !x.hideFromUsers) : items.itemLookupItems;

        const today = new Date();
        return filteredItems.map(({ id, name, displayName, options, itemLookupTypeId, supplierId, autotaskCode, salesforceCode, upfrontSalesforceCode, recurringSalesforceCode, isInternal, notes, customAttributes, typeCustomAttributes, disabled, createdAt, updatedAt, itemLookupTags, itemLookupVersions, moduleRevisionLookupItems, hideFromUsers, exemptFromMarginWarnings, hideOnQuoteByDefault, isCreditItem }) => {
            let closestPastVersion = null;
            let itemLookupVersionsWithState = [];

            if (itemLookupVersions.length > 0) {
                closestPastVersion = getClosestPastVersion(itemLookupVersions);

                itemLookupVersionsWithState = itemLookupVersions.map((version) => {
                    return {
                        ...version,
                        state: (closestPastVersion && version.id === closestPastVersion.id) ? "current" : new Date(version.activeStartDate) > new Date() ? "future" : "locked"
                    }
                })
            }

            const itemLookupType = itemLookupTypesData.find(w => w.id === itemLookupTypeId)
            const supplier = supplierId !== 0 ? suppliersData.find(w => w.id === supplierId) : null

            return {
                id,
                itemLookupId: id, // We need Id in two columns.  One to show the id, and the other used for adding the item to the basket.
                name,
                displayName,
                options,
                itemLookupTypeId,
                itemLookupTypeName: itemLookupType ? itemLookupType.itemLookupTypeName : null,
                supplierId,
                supplierName: supplier ? supplier.supplierName : null,
                autotaskCode,
                salesforceCode,
                upfrontSalesforceCode,
                recurringSalesforceCode,
                isInternal,
                notes,
                customAttributes,
                typeCustomAttributes: typeCustomAttributes ? JSON.parse(typeCustomAttributes) : [],
                disabled,
                createdAt: createdAt ? formatDateTime(createdAt) : null,
                updatedAt: updatedAt ? formatDateTime(updatedAt) : null,
                tags: itemLookupTags.length > 0 ? itemLookupTags.map((tag) => (tagsData.find(w => w.id === tag.tagId))).filter(f => f) : [],
                versions: itemLookupVersionsWithState,
                moduleRevisionLookupItems: moduleRevisionLookupItems,
                activeVersion: closestPastVersion ? closestPastVersion.id : null,
                hideFromUsers,
                exemptFromMarginWarnings,
                hideOnQuoteByDefault,
                isCreditItem
            };
        });
    }

    // Fetch items using useQuery
    const {
        data: itemsData,
        isLoading: isLoadingItems,
        error: itemsError,
        refetch: refetchItems,
        isRefetching: isRefetchingItems
    } = useQuery({
        queryKey: ["itemlookups", mode],
        queryFn: getItems,
        enabled: !!itemLookupTypesData && !!tagsData && !!suppliersData, // Only run when lookuptypes, tags and suppliers are available
        staleTime: 5000 // Needs to be fairly short period of caching, because we want the user to see the new items when in the flowbuilder
    });

    const itemLookupTypeOptions = itemLookupTypesData || [];
    const tagsOptions = tagsData || [];

    useEffect(() => {
        if (moduleRevisionId) {
            setFilter(prev => ({ ...prev, moduleRevisionId: moduleRevisionId, pageNumber: 1 }));
        }
    }, []);

    // When itemsData is fetched, update our local state.
    useEffect(() => {
        if (itemsData) {
            setItems(itemsData);
        }
    }, [itemsData]);

    useEffect(() => {
        if (itemsData) {
            refetchItems();
        }
    }, [filter]);

    const changeFilter = (obj) => {
        const filterId = obj.filterId
        const value = obj.value

        if (filterId === "pageNumber") {
            setFilter(prev => ({ ...prev, [filterId]: value }));
        }
        else if (filterId === "onlyShowActiveItems") {
            if (typeof value === 'object')
                setFilter(prev => ({ ...prev, [filterId]: value.value }));
            else
                setFilter(prev => ({ ...prev, [filterId]: value }));
        }
        else if (filterId === "itemLookupTypeName") {
            if (value === undefined) {
                setFilter(({ itemLookupTypeId, ...rest }) => ({ ...rest, pageNumber: 1 }));
            } else {
                const itemLookupType = itemLookupTypesData.find((i) => i.itemLookupTypeName === value);
                setFilter(prev => ({ ...prev, itemLookupTypeId: itemLookupType.id, pageNumber: 1 }));
            }
        } else if (filterId === "tags") {
            if (value === undefined) {
                setFilter(({ itemLookupTagId, ...rest }) => ({ ...rest, pageNumber: 1 }));
            } else {
                setFilter(prev => ({ ...prev, itemLookupTagId: value, pageNumber: 1 }));
            }
        } else if (filterId === "thisModuleItems") {
            if (value === false) {
                setFilter(({ moduleRevisionId, ...rest }) => ({ ...rest, pageNumber: 1 }));
            } else {
                setFilter(prev => ({ ...prev, moduleRevisionId: moduleRevisionId, pageNumber: 1 }));
            }
        } else if (filterId === "searchTerm") {
            if (value === '') {
                setFilter(({ searchOptions, ...rest }) => ({ ...rest, pageNumber: 1 }));
            } else {
                const searchableFields = Object.entries(visibleColumns).filter(([_, value]) => value === true).map(([key]) => key);
                let searchOptions = { searchableFields: searchableFields, searchTerm: value, fieldSearchTerms: [] }

                // try to find item lookup type name in search parameter
                // if found, add the itemLookupTypeIds to the searchOptions
                if (searchableFields.find(w => w === "itemLookupTypeName") && itemLookupTypesData) {
                    const itemLookupTypeIds = itemLookupTypesData.filter(w => w.itemLookupTypeName.toLowerCase().includes(value.toLowerCase())).map(u => u.id.toString());
                    if (itemLookupTypeIds.length > 0) {
                        searchOptions.fieldSearchTerms.push({ "fieldName": "itemLookupTypeName", "values": itemLookupTypeIds })
                    }
                }

                // try to find tags in search parameter
                // if found, add the tagIds to the searchOptions
                if (searchableFields.find(w => w === "tags") && tagsData) {
                    const tagIds = tagsData.filter(w => w.tagName.toLowerCase().includes(value.toLowerCase())).map(u => u.id.toString());
                    if (tagIds.length > 0) {
                        searchOptions.fieldSearchTerms.push({ "fieldName": "tags", "values": tagIds })
                    }
                }

                // try to find the words 'internal' and 'external' in search parameter
                // if found, add 'isInternal' to the searchOptions
                if (searchableFields.find(w => w === "isInternal")) {
                    const internalPossibleValues = ["i", "in", "int", "inte", "inter", "intern", "interna", "internal"];
                    const externalPossibleValues = ["e", "ex", "ext", "exte", "exter", "extern", "externa", "external"]
                    if (internalPossibleValues.includes(value.trim().toLowerCase())) {
                        searchOptions.fieldSearchTerms.push({ "fieldName": "isInternal", "values": ["true"] })
                    } else if (externalPossibleValues.includes(value.trim().toLowerCase())) {
                        searchOptions.fieldSearchTerms.push({ "fieldName": "isInternal", "values": ["false"] })
                    }
                }

                // try to find the words 'true' and 'false' in search parameter
                // if found, add 'disabled' to the searchOptions
                if (searchableFields.find(w => w === "disabled")) {
                    const disabledPossibleValues = ["y", "yes"];
                    const enabledPossibleValues = ["n", "no"];
                    if (disabledPossibleValues.includes(value.trim().toLowerCase())) {
                        searchOptions.fieldSearchTerms.push({ "fieldName": "disabled", "values": ["true"] })
                    } else if (enabledPossibleValues.includes(value.trim().toLowerCase())) {
                        searchOptions.fieldSearchTerms.push({ "fieldName": "disabled", "values": ["false"] })
                    }
                }

                setFilter(prev => ({ ...prev, searchOptions: searchOptions, pageNumber: 1 }));
            }
        } else {
            setFilter(prev => ({ ...prev, [filterId]: value, pageNumber: 1 }));
        }
    }

    // Handle row click to open modal with item details
    const handleRowClick = (item) => {
        if (mode.toLowerCase() === "user" || mode.toLowerCase() === "import") return;

        setSelectedItem(item);
        setSelectedItemModal(true);         // Open selected item modal
    };

    const handleNewItemClick = () => {
        const newItem = {
            "id": 0, "name": null, "displayName": null, "options": null, "itemLookupTypeId": null, "supplierId": null, "autotaskCode": null, "salesforceCode": null,
            "isInternal": true, "notes": null, "customAttributes": null, "typeCustomAttributes": null, "isImported": false, "disabled": false,
            "versions": [],
            "tags": [], "activeVersion": 0,
            "moduleRevisionLookupItems": [],
            "hideFromUsers": false,
            "exemptFromMarginWarnings": false,
            "hideOnQuoteByDefault": false,
            "isCreditItem": false
        }
        setItems([...items, newItem]); // Add new item to items array (newItem)
        setSelectedItem(newItem);
        setSelectedItemModal(true);
    }

    const handleItemChangeFromModal = (item, isNewItem) => {
        setSelectedItem(item)

        if (isNewItem) {
            setItems([...items, item]);
        } else {
            setItems(items.map((i) => i.id === item.id ? item : i));
        }
    }

    const onToggle = () => {
        setItems((prevItems) => prevItems.filter(i => i.id !== 0))
        setSelectedItemModal(!selectedItemModal)
    }

    const toggleImportModal = () => setImportModalOpen((s) => !s);

    const removeNullOldValues = (audits) => {
        const transformed = {};

        for (const [key, value] of Object.entries(audits)) {
            if (value.old === null || value.old === undefined) {
                transformed[key] = value.new;
            } else {
                transformed[key] = value;
            }
        }

        return transformed;
    };

    const handleExportClick = async () => {
        if (process.env.REACT_APP_USELOCALDATA === "true") {
            toast.warning("Option not available in local mode (requires iQuoteApi)");
            return;
        }

        setExportButtonIsEnabled(false);

        let downloadFilter = { ...filter }
        delete downloadFilter.pageSize
        delete downloadFilter.pageNumber

        const response = await exportItems(downloadFilter)
        downloadFile(response, "Items.xlsx");

        setExportButtonIsEnabled(true);
    }

    const handleImportTemplateClick = async () => {
        const response = await downloadItemsTemplate();
        downloadFile(response, "ItemsTemplate.xlsx");
    }

    const handleImportClick = () => {
        if (process.env.REACT_APP_USELOCALDATA === "true") {
            toast.warning("Option not available in local mode (requires iQuoteApi)");
            return;
        }

        setImportModalOpen(true);
    }

    const handleAddItemToBasketClicked = (itemLookupId) => {
        const item = itemsData.find(x => x.id === itemLookupId)
        const activeVersion = item.versions.find(w => w.id === item.activeVersion)

        if (!activeVersion || !(activeVersion.isUpfront || activeVersion.isRecurring)) {
            setIsIncompleteCatalogueItemDialogOpen(true);
            return;
        }
        onAddItemToBasketClicked?.(itemLookupId)
    }

    const getActions = () => {
        var items = []

        if (mode.toLowerCase() === "admin") {
            // If viewing the table in admin-mode, then we need to allow the user to add new items to the catalog

            items.push(
                <Button onClick={() => handleNewItemClick()}>
                    Add New Item
                </Button>,
            )

            if (!moduleRevisionId) {
                // If not looking at module items, then we also allow bulk import/export
                items.push(
                    <Button onClick={() => handleExportClick()} disabled={!exportButtonIsEnabled}>
                        Export
                    </Button>,
                    <Dropdown outline isOpen={dropdownOpen} toggle={toggle}>
                        <DropdownToggle caret>Import</DropdownToggle>
                        <DropdownMenu>
                            <DropdownItem onClick={() => handleImportTemplateClick()}>Download Import Template</DropdownItem>
                            <DropdownItem onClick={() => handleImportClick()}>Import</DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                )
            }

        }

        // Finally, we add cog icon for configuring the shown columns.
        items.push(
            <Button outline color={theme === "dark-mode" ? "dark" : "light"} onClick={toggleSettingsModal}>
                <i className="bi bi-gear fs-5"></i>
            </Button>
        )

        return items;
    }

    // Define the columns for the DataTable
    const columns = [
        {
            key: "id",
            label: "ID",
            isDefault: true,
            system: true
        },
        {
            key: "name",
            label: "Name",
            isDefault: true,
            system: true
        },
        {
            key: "options",
            label: "Options",
            isDefault: false
        },
        {
            key: "autotaskCode",
            label: "Autotask Code",
            isDefault: true
        },
        {
            key: "salesforceCode",
            label: "Salesforce Code",
            isDefault: false,
            render: (value, row) => {   
                const currentVersion = row.versions.find(w => w.id === row.activeVersion);
                const upfrontSalesforceCode = currentVersion?.upfrontSalesforceCode || row.upfrontSalesforceCode;
                const recurringSalesforceCode = currentVersion?.recurringSalesforceCode || row.recurringSalesforceCode;
                if (currentVersion?.isUpfront && currentVersion?.isRecurring && upfrontSalesforceCode && recurringSalesforceCode && upfrontSalesforceCode !== recurringSalesforceCode) {
                    return <span>{upfrontSalesforceCode} /<br/>{recurringSalesforceCode}</span>
                } else if (currentVersion?.isUpfront) {
                    return <span>{upfrontSalesforceCode}</span>
                } else if (currentVersion?.isRecurring) {
                    return <span>{recurringSalesforceCode}</span>
                } else {
                    return <span>{value}</span>
                }
            }
        },
        {
            key: "itemLookupTypeName",
            label: "Type",
            isDefault: true,
        },
        {
            key: "supplierName",
            label: "Supplier",
            isDefault: true
        },
        {
            key: "tags",
            label: "Tags",
            isDefault: !!moduleRevisionId || true,
            render: (value, row) => (
                <TagsList tags={row.tags} />
            )
        },
        {
            key: "isInternal",
            label: "Int/Ext",
            isDefault: false,
            render: (value) => (
                <span>{value ? "Internal" : "External"}</span>
            )
        },
        {
            key: 'isCreditItem',
            label: 'Credit Item',
            isDefault: false,
            render: (value) => (
                <span>{value ? "Yes" : "No"}</span>
            )
        },
        {
            key: "createdAt",
            label: "Created",
            isDefault: false
        },
        {
            key: "updatedAt",
            label: "Updated",
            isDefault: false
        },
        {
            key: "upfrontCost",
            label: "Upfront Cost",
            className: "p-1 fixed-col-100",
            isDefault: !moduleRevisionId,
            render: (value, row) => (
                <>
                    {row.isInternal === true && row.versions.find(w => w.id === row.activeVersion) !== undefined ? (
                        <InputGroup>
                            <InputGroupText>£</InputGroupText>
                            <Input disabled className="px-3 text-end" type="text" value={formatCurrency(row.versions.find(w => w.id === row.activeVersion).upfrontCost)} />
                        </InputGroup>
                    ) : null}

                    {row.isInternal === false && row.versions.find(w => w.id === row.activeVersion) !== undefined ? (
                        <InputGroup>
                            <InputGroupText>£</InputGroupText>
                            <Input disabled className="px-3 text-end" type="text" value={formatCurrency(row.versions.find(w => w.id === row.activeVersion).upfrontCost)} />
                        </InputGroup>
                    ) : null}
                </>
            )
        },
        {
            key: "upfrontPrice",
            label: "Upfront Price",
            className: "p-1 fixed-col-100",
            isDefault: !moduleRevisionId,
            render: (value, row) => (
                <>
                    {row.isInternal === true && row.versions.find(w => w.id === row.activeVersion) !== undefined ? (
                        <InputGroup>
                            <InputGroupText>£</InputGroupText>
                            <Input disabled className="px-3 text-end" type="text" value={formatCurrency(row.versions.find(w => w.id === row.activeVersion).upfrontPrice)} />
                        </InputGroup>
                    ) : null}

                    {row.isInternal === false && row.versions.find(w => w.id === row.activeVersion) !== undefined ? (
                        <InputGroup>
                            <InputGroupText>£</InputGroupText>
                            <Input disabled className="px-3 text-end" type="text" readOnly value={formatCurrency(row.versions.find(w => w.id === row.activeVersion).upfrontPrice)} />
                        </InputGroup>
                    ) : null}
                </>
            )
        },
        {
            key: "upfrontRecognitionMonths",
            label: "Upfront Recognition Months",
            className: "p-1 fixed-col-100",
            render: (value, row) => {
                const currentVersion = row.versions.find(w => w.id === row.activeVersion);
                return(
                    <>
                        {currentVersion?.isUpfront && spreadableUpfrontSalesforceCodeNames.has(currentVersion?.upfrontSalesforceCode) ? (
                            <InputGroup>
                                <Input disabled className="px-3 text-end" type="text" readOnly value={currentVersion.upfrontRecognitionMonths ?? ""} />
                            </InputGroup>
                        ) : null}
                    </>
                )
            }
        },        
        {
            key: "upfrontCostDefaultDiscount",
            label: "Upfront Cost Default Discount",
            className: "p-1 fixed-col-100",
            isDefault: false,
            render: (value, row) => (
                <>
                    {row.versions.find(w => w.id === row.activeVersion) !== undefined ? (
                        <InputGroup>
                            <Input className="px-3 text-end" type="text" disabled value={formatCurrency(row.versions.find(w => w.id === row.activeVersion).upfrontCostDefaultDiscount)} />
                            <InputGroupText>%</InputGroupText>
                        </InputGroup>
                    ) : null}
                </>
            )
        },
        {
            key: "upfrontCostDiscountThreshold",
            label: "Upfront Cost Discount Threshold",
            className: "p-1 fixed-col-100",
            isDefault: false,
            render: (value, row) => (
                <>
                    {row.versions.find(w => w.id === row.activeVersion) !== undefined ? (
                        <InputGroup>
                            <Input className="px-3 text-end" type="text" disabled value={formatCurrency(row.versions.find(w => w.id === row.activeVersion).upfrontCostDiscountThreshold)} />
                            <InputGroupText>%</InputGroupText>
                        </InputGroup>
                    ) : null}
                </>
            )
        },
        {
            key: "upfrontPriceDefaultDiscount",
            label: "Upfront Price Default Discount",
            className: "p-1 fixed-col-100",
            isDefault: false,
            render: (value, row) => (
                <>
                    {row.versions.find(w => w.id === row.activeVersion) !== undefined ? (
                        <InputGroup>
                            <Input className="px-3 text-end" type="text" disabled value={formatCurrency(row.versions.find(w => w.id === row.activeVersion).upfrontPriceDefaultDiscount)} />
                            <InputGroupText>%</InputGroupText>
                        </InputGroup>
                    ) : null}
                </>
            )
        },
        {
            key: "upfrontPriceDiscountThreshold",
            label: "Upfront Price Discount Threshold",
            className: "p-1 fixed-col-100",
            isDefault: false,
            render: (value, row) => (
                <>
                    {row.versions.find(w => w.id === row.activeVersion) !== undefined ? (
                        <InputGroup>
                            <Input className="px-3 text-end" type="text" disabled value={formatCurrency(row.versions.find(w => w.id === row.activeVersion).upfrontPriceDiscountThreshold)} />
                            <InputGroupText>%</InputGroupText>
                        </InputGroup>
                    ) : null}
                </>
            )
        },
        {
            key: "recurringCost",
            label: "Recurring Cost",
            className: "p-1 fixed-col-100",
            isDefault: !moduleRevisionId,
            render: (value, row) => (
                <>
                    {row.versions.find(w => w.id === row.activeVersion) !== undefined ? (
                        <InputGroup>
                            <InputGroupText>£</InputGroupText>
                            <Input className="px-3 text-end" type="text" disabled value={formatCurrency(row.versions.find(w => w.id === row.activeVersion).recurringCost)} />
                        </InputGroup>

                    ) : null}
                </>
            )
        },
        {
            key: "recurringPrice",
            label: "Recurring Price",
            className: "p-1 fixed-col-100",
            isDefault: !moduleRevisionId,
            render: (value, row) => (
                <>
                    {row.versions.find(w => w.id === row.activeVersion) !== undefined ? (
                        <InputGroup>
                            <InputGroupText>£</InputGroupText>
                            <Input className="px-3 text-end" type="text" disabled value={formatCurrency(row.versions.find(w => w.id === row.activeVersion).recurringPrice)} />
                        </InputGroup>
                    ) : null}
                </>
            )
        },
        {
            key: "recurringStartMonth",
            label: "Recurring Start Month",
            isDefault: !moduleRevisionId,
            render: (value, row) => (
                <>
                    {row.versions.find(w => w.id === row.activeVersion) !== undefined ? (
                        <InputGroup>
                            <Input className="px-3 text-end" type="text" disabled value={row.versions.find(w => w.id === row.activeVersion).recurringStartMonth} />
                        </InputGroup>
                    ) : null}
                </>
            )
        },
        {
            key: "recurringNumPayments",
            label: "Recurring Num Payments",
            isDefault: !moduleRevisionId,
            render: (value, row) => (
                <>
                    {row.versions.find(w => w.id === row.activeVersion) !== undefined ? (
                        <InputGroup>
                            <Input className="px-3 text-end" type="text" disabled value={row.versions.find(w => w.id === row.activeVersion).recurringNumPayments} />
                        </InputGroup>
                    ) : null}
                </>
            )
        },
        {
            key: "recurringIntervalMonths",
            label: "Recurring Interval Months",
            isDefault: !moduleRevisionId,
            render: (value, row) => (
                <>
                    {row.versions.find(w => w.id === row.activeVersion) !== undefined ? (
                        <InputGroup>
                            <Input className="px-3 text-end" type="text" disabled value={row.versions.find(w => w.id === row.activeVersion).recurringIntervalMonths} />
                        </InputGroup>
                    ) : null}
                </>
            )
        },
        {
            key: "recurringCostDefaultDiscount",
            label: "Recurring Cost Default Discount",
            className: "p-1 fixed-col-100",
            isDefault: false,
            render: (value, row) => (
                <>
                    {row.versions.find(w => w.id === row.activeVersion) !== undefined ? (
                        <InputGroup>
                            <Input className="px-3 text-end" type="text" disabled value={formatCurrency(row.versions.find(w => w.id === row.activeVersion).recurringCostDefaultDiscount)} />
                            <InputGroupText>%</InputGroupText>
                        </InputGroup>
                    ) : null}
                </>
            )
        },
        {
            key: "recurringCostDiscountThreshold",
            label: "Recurring Cost Discount Threshold",
            className: "p-1 fixed-col-100",
            isDefault: false,
            render: (value, row) => (
                <>
                    {row.versions.find(w => w.id === row.activeVersion) !== undefined ? (
                        <InputGroup>
                            <Input className="px-3 text-end" type="text" disabled value={formatCurrency(row.versions.find(w => w.id === row.activeVersion).recurringCostDiscountThreshold)} />
                            <InputGroupText>%</InputGroupText>
                        </InputGroup>
                    ) : null}
                </>
            )
        },
        {
            key: "recurringPriceDefaultDiscount",
            label: "Recurring Price Default Discount",
            className: "p-1 fixed-col-100",
            isDefault: false,
            render: (value, row) => (
                <>
                    {row.versions.find(w => w.id === row.activeVersion) !== undefined ? (
                        <InputGroup>
                            <Input className="px-3 text-end" type="text" disabled value={formatCurrency(row.versions.find(w => w.id === row.activeVersion).recurringPriceDefaultDiscount)} />
                            <InputGroupText>%</InputGroupText>
                        </InputGroup>
                    ) : null}
                </>
            )
        },
        {
            key: "recurringPriceDiscountThreshold",
            label: "Recurring Price Discount Threshold",
            className: "p-1 fixed-col-100",
            isDefault: false,
            render: (value, row) => (
                <>
                    {row.versions.find(w => w.id === row.activeVersion) !== undefined ? (
                        <InputGroup>
                            <Input className="px-3 text-end" type="text" disabled value={formatCurrency(row.versions.find(w => w.id === row.activeVersion).recurringPriceDiscountThreshold)} />
                            <InputGroupText>%</InputGroupText>
                        </InputGroup>
                    ) : null}
                </>
            )
        },
        {
            key: "disabled",
            label: "Disabled",
            isDefault: false,
            render: (value) => (
                <span>{value ? "Yes" : "No"}</span>
            )
        },
        ...(moduleRevisionId ? [{
            key: "AddToModuleRevision",
            label: "Linked To This Module",
            isDefault: true,
            system: true,
            render: (value, row) => (
                <FormGroup switch>
                    <Input
                        className="px-3 text-end mx-2"
                        type="switch"
                        role="switch"
                        checked={row.moduleRevisionLookupItems.find(w => w.moduleRevisionId === moduleRevisionId) !== undefined}
                        onClick={(e) => e.stopPropagation()} // 🔐 This will prevent row click
                        onChange={async (e) => {
                            if (e.target.checked) {
                                var result = await addModuleRevisionLookupItem(moduleRevisionId, row.id)
                                if (result.message !== "Module Revision Lookup Item created successfully") {
                                    alert("Failed to add entry")
                                } else {
                                    setItems((prev) => prev.map(x => x.id !== row.id ? x : { ...x, moduleRevisionLookupItems: [...x.moduleRevisionLookupItems, result.moduleRevisionLookupItemDTO] }))
                                    onItemAddedToModule?.(row)
                                }
                            } else {
                                var entryToRemove = row.moduleRevisionLookupItems.find(w => w.moduleRevisionId === moduleRevisionId)

                                if (entryToRemove) {
                                    var result = await removeModuleRevisionLookupItem(entryToRemove.id)

                                    if (result.message !== "success") {
                                        alert("Failed to remove entry")
                                    } else {
                                        setItems((prev) => prev.map(x => x.id !== row.id ? x : { ...x, moduleRevisionLookupItems: x.moduleRevisionLookupItems.filter(x => x.id !== entryToRemove.id) }))
                                        onItemRemovedFromModule?.(entryToRemove.itemLookupId)
                                    }
                                } else {
                                    alert("Failed to find entry to remove")
                                }
                            }
                        }}
                    />
                </FormGroup>
            )
        }] : []),
        ...(mode.toLowerCase() === "user" ? [{
            key: "itemLookupId",
            label: "",
            isDefault: true,
            system: true,
            className: "fixed-col-75 text-end",
            render: (value, row) => (
                <Button className={lockState?.isLocked ? "locked" : ""}
                    onClick={
                        (e) => {
                            e.stopPropagation();
                            handleAddItemToBasketClicked(value)
                        }
                    }
                >
                    <i className="bi bi-cart-plus"></i>
                </Button>
            )
        }] : []),
        ...(mode.toLowerCase() === "import" ? [{
            key: "itemLookupId",
            label: "",
            isDefault: true,
            system: true,
            className: "fixed-col-75 text-end",
            render: (value, row) => (
                <Button
                    onClick={
                        (e) => {
                            e.stopPropagation();
                            onAddItemToImport(row.id)

                        }
                    }
                >
                    Select
                </Button>
            )
        }] : []),
    ];

    //configure the DataTable to display items
    const defaultVisibleColumns = columns.reduce((acc, col) => {
        acc[col.key] = !!col.isDefault;
        return acc;
    }, {});

    const [visibleColumns, setVisibleColumns] = useState(() => {
        const stored = localStorage.getItem(COLUMN_SETTINGS_KEY);
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                const merged = { ...defaultVisibleColumns, ...parsed }; // ensures new columns added
                return merged;
            } catch {
                return defaultVisibleColumns;
            }
        }
        return defaultVisibleColumns;
    });


    return (
        <>
            {/* DataTable for displaying items */}
            <DataTablePageQuery
                columns={columns.filter(col => visibleColumns[col.key])}
                data={items}
                totalRecords={totalRecords}
                onRowClick={(row) => handleRowClick(row)}
                onFilterChange={changeFilter}
                isLoading={isLoadingItems || isLoadingItemLookupTypes || isLoadingTags || isLoadingSuppliers || isRefetchingItems}
                error={itemsError || itemLookupTypesError || tagsError || suppliersError}
                exportToCsv={false}
                sortConfiguration={{ key: "id", direction: "descending" }}
                filters={[
                    {
                        id: "tags",
                        label: "Tag",
                        type: "select",
                        isSearchable: true,
                        advanced: true,
                        options: tagsOptions.map((item) => ({
                            value: item.id,
                            label: item.tagName
                        })),
                        size: "small"
                    },
                    {
                        id: "createdAt",
                        label: "Created",
                        type: "daterange",
                        advanced: true
                    },
                    {
                        id: "autotaskCode",
                        label: "Autotask Code",
                        type: "text",
                        advanced: true
                    },
                    {
                        id: "updatedAt",
                        label: "Updated",
                        type: "daterange",
                        advanced: true
                    },
                    {
                        id: "salesforceCode",
                        label: "Salesforce Code",
                        type: "text",
                        advanced: true
                    },
                    {
                        id: "activeCost",
                        label: "Active Cost",
                        type: "numberrange",
                        advanced: true,
                        step: "0.1"
                    },
                    {
                        id: "supplierId",
                        label: "Supplier",
                        type: "select",
                        isSearchable: true,
                        advanced: true,
                        options: suppliersData && suppliersData.map((item) => ({
                            value: item.id,
                            label: item.supplierName
                        })),
                        size: "small"
                    },
                    {
                        id: "activePrice",
                        label: "Active Price",
                        type: "numberrange",
                        advanced: true,
                        step: "0.1"
                    },
                    {
                        id: "active",
                        label: "Recurrence",
                        type: "select",
                        advanced: true,
                        options: [{ value: "Upfront", label: "Upfront" }, { value: "Recurring", label: "Recurring" }, { value: "Both", label: "Both" }],
                        size: "small"
                    },
                    {
                        id: "activeDiscount",
                        label: "Active Discount",
                        type: "numberrange",
                        advanced: true,
                        step: "0.1"
                    },
                    {
                        id: "isInternal",
                        label: "Is Internal?",
                        type: "switch",
                        defaultValue: false,
                        advanced: true
                    },
                    {
                        id: "onlyShowActiveItems",
                        label: "Active/Disabled?",
                        type: "select",
                        defaultValue: { value: "Active", label: "Active" },
                        isDisabled: !allowShowingInactiveItems,
                        options: ["All", "Active", "Disabled"].map((item) => ({
                            value: item,
                            label: item
                        })),
                        advanced: true
                    },
                    {
                        id: "isCreditItem",
                        label: "Is Credit Item?",
                        type: "switch",
                        defaultValue: false,
                        advanced: true
                    },
                    {
                        id: "itemLookupTypeName",
                        label: "Lookup Type",
                        isSearchable: true,
                        type: "select",
                        options: itemLookupTypeOptions?.map((item) => ({
                            value: item.itemLookupTypeName,
                            label: item.itemLookupTypeName
                        }))
                    },
                    ...(moduleRevisionId ? [{
                        id: "thisModuleItems",
                        label: "This Module's Items",
                        type: "switch",
                        defaultValue: true,
                        advanced: false
                    }] : [])
                ]}
                actions={getActions()}
            />

            {/* Modal for displaying selected item details */}
            <Modal isOpen={selectedItemModal} centered size="xl" fullscreen={fullscreen} scrollable backdrop="static"
            >
                {selectedItem && <ItemModal toggle={() => onToggle()} item={selectedItem}
                    suppliersData={suppliersData?.map((item) => ({
                        value: item.id,
                        label: item.supplierName
                    }))}
                    itemLookupTypesOptions={itemLookupTypesData?.map((item) => ({
                        value: item.id,
                        label: item.itemLookupTypeName
                    }))}
                    itemLookupTypesData={itemLookupTypesData}
                    tagsData={tagsData || []}
                    onItemChange={(updatedItem, isNewItem) => handleItemChangeFromModal(updatedItem, isNewItem)}
                    moduleRevisionId={moduleRevisionId}
                    toggleFullscreen={toggleFullscreen}
                    fullscreen={fullscreen}
                />}
            </Modal>

            {/* Modal for importing items */}
            <ItemBulkImport
                isOpen={importModalOpen}
                onClose={toggleImportModal}
                onImported={refetchItems}
            />

            {/* Settings Modal */}
            <Modal isOpen={settingsModalOpen} toggle={toggleSettingsModal} scrollable centered backdrop="static">
                <ModalHeader toggle={toggleSettingsModal}>Table Settings</ModalHeader>
                <ModalBody>
                    {columns.map((col) => (
                        <FormGroup switch key={col.key}>
                            <Input
                                type="switch"
                                role="switch"
                                id={`switch-${col.key}`}
                                checked={visibleColumns[col.key]}
                                disabled={col.system} // 🔒 disable if it's a system column
                                onChange={(e) => {
                                    const updated = {
                                        ...visibleColumns,
                                        [col.key]: e.target.checked
                                    };
                                    setVisibleColumns(updated);
                                    localStorage.setItem(COLUMN_SETTINGS_KEY, JSON.stringify(updated));
                                }}
                            />
                            <Label check htmlFor={`switch-${col.key}`}>
                                {col.label || "Add To Basket"} {col.system && <small className="text-muted">(Required)</small>}
                            </Label>
                        </FormGroup>
                    ))}
                    <br />
                    <Button outline color="danger" size="sm" onClick={() => {
                        setVisibleColumns(defaultVisibleColumns);
                        localStorage.setItem(COLUMN_SETTINGS_KEY, JSON.stringify(defaultVisibleColumns));
                    }}>
                        Reset to Defaults
                    </Button>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleSettingsModal}>Close</Button>
                </ModalFooter>
            </Modal>

            {/* Incomplete catalogue item dialog */}
            <DialogBox
                isOpen={isIncompleteCatalogueItemDialogOpen}
                title="Cannot Add Item to Basket"
                message={
                    <div className="d-flex align-items-center">
                        <i className="bi bi-exclamation-triangle-fill text-warning fs-1 me-3"></i>
                        <div style={{ display: "block" }}>
                            <div className="mb-3 fw-bold" >This catalogue item is incomplete.</div>
                            <div className="mb-3" >It must have valid commercials and be marked as either <strong>upfront</strong> and/or <strong>recurring</strong>.</div>
                            <div>Please speak to the product team to resolve this issue.</div>
                        </div>
                    </div>
                }
                toggle={() => setIsIncompleteCatalogueItemDialogOpen(false)}
                buttons={[
                    { label: "Ok", color: "primary", onClick: () => setIsIncompleteCatalogueItemDialogOpen(false) }
                ]}
            />
        </>
    );
}
export default ItemsTable;